const AVAILABILITY_TIMEOUT_MS = 10000;
const SUBMISSION_TIMEOUT_MS = 45000;
const DEFAULT_SUCCESS_MESSAGE = 'Thank you. The clinic team will confirm the next step by phone, email, or WhatsApp within one working day.';
const DEFAULT_ERROR_MESSAGE = 'We could not send the request right now. Please call or WhatsApp the clinic and try again later.';
const SUBMISSION_TIMEOUT_MESSAGE =
  'The clinic may have received this request, but the browser did not receive confirmation in time. Please check your email or call/WhatsApp before submitting again.';
const DEFAULT_AVAILABILITY_ERROR_MESSAGE = 'We could not check live availability right now. The clinic will still verify the requested time.';

async function readJsonResponse(response) {
  const contentType = response.headers.get('content-type') || '';

  if (!contentType.includes('application/json')) {
    const error = new Error('The booking endpoint returned an invalid response.');
    error.status = response.status;
    error.code = 'INVALID_ENDPOINT_RESPONSE';
    throw error;
  }

  const responseBody = await response.json().catch(() => null);
  if (!responseBody || typeof responseBody !== 'object') {
    const error = new Error('The booking endpoint returned an unreadable response.');
    error.status = response.status;
    error.code = 'INVALID_ENDPOINT_RESPONSE';
    throw error;
  }

  return responseBody;
}

function getEndpoint(kind) {
  if (kind === 'lead') return import.meta.env.VITE_LEAD_ENDPOINT || import.meta.env.VITE_BOOKING_ENDPOINT || '';
  return import.meta.env.VITE_BOOKING_ENDPOINT || '';
}

function getAvailabilityEndpoint() {
  const explicitEndpoint = import.meta.env.VITE_AVAILABILITY_ENDPOINT || '';
  if (explicitEndpoint) return explicitEndpoint;

  const bookingEndpoint = getEndpoint('booking');
  if (!bookingEndpoint) return '';

  const derivedEndpoint = bookingEndpoint.replace(/booking\.php(?:\?.*)?$/i, 'availability.php');
  return derivedEndpoint === bookingEndpoint ? '' : derivedEndpoint;
}

function localSubmissionKey(kind) {
  return `my-dentist:${kind}:pending-submissions`;
}

function saveLocalDraft(kind, payload) {
  const key = localSubmissionKey(kind);
  const existing = JSON.parse(window.localStorage.getItem(key) || '[]');
  const next = [
    {
      ...payload,
      localDraftId: crypto.randomUUID(),
      localSavedAt: new Date().toISOString()
    },
    ...existing
  ].slice(0, 20);

  window.localStorage.setItem(key, JSON.stringify(next));
}

export async function fetchBookingAvailability(date) {
  const endpoint = getAvailabilityEndpoint();

  if (!endpoint || !date) {
    return {
      ok: true,
      enabled: false,
      unavailableSlots: []
    };
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), AVAILABILITY_TIMEOUT_MS);

  try {
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('date', date);

    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'omit',
      signal: controller.signal
    });

    const responseBody = await readJsonResponse(response);

    if (!response.ok || responseBody?.ok !== true) {
      throw new Error(responseBody?.message || `Availability endpoint returned ${response.status}`);
    }

    return {
      ok: true,
      enabled: Boolean(responseBody?.availabilityEnabled),
      unavailableSlots: Array.isArray(responseBody?.unavailableSlots) ? responseBody.unavailableSlots : []
    };
  } catch (error) {
    console.error('[booking] availability check failed', error);
    return {
      ok: false,
      enabled: false,
      unavailableSlots: [],
      message: DEFAULT_AVAILABILITY_ERROR_MESSAGE
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function submitRequest(kind, payload) {
  const endpoint = getEndpoint(kind);
  const submission = {
    ...payload,
    source: kind,
    submittedAt: new Date().toISOString(),
    pagePath: window.location.pathname
  };

  if (!endpoint) {
    saveLocalDraft(kind, submission);
    return {
      ok: false,
      mode: 'local-draft',
      message:
        'Your details were validated and saved in this browser, but the clinic booking endpoint is not connected yet. Please call or WhatsApp the clinic to confirm.'
    };
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), SUBMISSION_TIMEOUT_MS);

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      signal: controller.signal,
      body: JSON.stringify(submission)
    });

    const responseBody = await readJsonResponse(response);

    if (!response.ok || responseBody?.ok !== true) {
      const error = new Error(responseBody?.message || `Endpoint returned ${response.status}`);
      error.status = response.status;
      error.code = responseBody?.error?.code || '';
      error.details = Array.isArray(responseBody?.error?.details) ? responseBody.error.details : [];
      throw error;
    }

    window.dataLayer?.push({
      event: kind === 'lead' ? 'treatment_lead_submitted' : 'booking_submitted',
      appointment_reason: payload.reason || payload.service || 'unknown'
    });

    return {
      ok: true,
      mode: 'endpoint',
      message: responseBody?.message || DEFAULT_SUCCESS_MESSAGE
    };
  } catch (error) {
    console.error(`[${kind}] submission failed`, error);
    const safeEndpointMessage = typeof error?.message === 'string' && error.message !== '' ? error.message : '';
    const shouldShowEndpointMessage =
      error?.code === 'SLOT_UNAVAILABLE' ||
      error?.code === 'VALIDATION_ERROR' ||
      (error?.status >= 400 && error?.status < 500);

    const wasAborted = error?.name === 'AbortError';
    let message = DEFAULT_ERROR_MESSAGE;
    if (wasAborted) {
      message = SUBMISSION_TIMEOUT_MESSAGE;
    } else if (shouldShowEndpointMessage && safeEndpointMessage) {
      message = safeEndpointMessage;
    }

    return {
      ok: false,
      mode: 'endpoint-error',
      code: wasAborted ? 'REQUEST_TIMEOUT' : error?.code || '',
      details: Array.isArray(error?.details) ? error.details : [],
      message
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}
